import { inject, Injectable } from '@angular/core';
import {
  type CreateEventCardInput,
  type EventCardTilesForExportQueryQueryVariables,
  type EventCardTilesQueryQueryVariables,
  type UpdateEventCardInput,
} from '@reconcept-platform/shared/types';
import { ToastNotificationsService } from '@reconcept-platform/shared/ui/core/toast-notifications';
import { map, tap } from 'rxjs/operators';
import { EventCardApi } from '../../../api/event-card/event-card.api';
import { type EventCardStatusEnum } from '../../../schema/schema-enums';

@Injectable({
  providedIn: 'root',
})
export class EventCardService {
  private readonly eventCardApi = inject(EventCardApi);
  private readonly toast = inject(ToastNotificationsService);

  watchEventCards(eventCardsFilter: EventCardTilesQueryQueryVariables) {
    return this.eventCardApi.watchEventCards(eventCardsFilter);
  }

  watchEventCardsForExport(eventCardsFilter: EventCardTilesForExportQueryQueryVariables) {
    return this.eventCardApi.watchEventCardsForExport(eventCardsFilter);
  }

  watchEventCard(eventId: string) {
    return this.eventCardApi.watchEventCard(eventId);
  }

  watchTimelineEventCards(status: EventCardStatusEnum[], workspaceIds: string[]) {
    return this.eventCardApi.watchTimelineEventCards(status, workspaceIds);
  }

  watchLinkedCards(goalCardId: string) {
    return this.eventCardApi.watchEventWithLinkedCards(goalCardId).pipe(map((eventCard) => eventCard.linkedCards));
  }

  createEvent(createEventCardInput: CreateEventCardInput) {
    return this.eventCardApi.createEventCard(createEventCardInput);
  }

  updateEvent(updateEventCardInput: UpdateEventCardInput, workspaceId?: string) {
    return this.eventCardApi.updateEventCard(updateEventCardInput, workspaceId).pipe(
      tap({
        error: (e) =>
          this.toast.errorTranslated({
            titleKey: 'qz-failed-zm',
            messageKey: e?.message ?? 'qz-unexpected-error-has-occurred-please-try-again-zm',
          }),
      }),
    );
  }

  destroyEventCard(eventCardId: string, workspaceId: string) {
    return this.eventCardApi.destroyEventCard(eventCardId, workspaceId);
  }

  duplicateEventCard(id: string, workspaceId: string) {
    return this.eventCardApi.duplicateEventCard(id, workspaceId);
  }
}
